import deepmerge from 'deepmerge';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next, useTranslation } from 'react-i18next';

import {
    availableI18nLanguages,
    defaultResources,
    i18nDefaultConfig,
    isAvailableLanguage,
} from '../../../shared/source/internationalization';

import type {
    LanguageConfig,
    LanguageConfigMap,
    LanguageKey,
    TranslationOverrides,
} from '../../../shared/source/internationalization';
import type i18next from 'i18next';
import type Maybe from 'true-myth/maybe';

export type InternationalizationDependencies = {
    readonly i18nProvider: Pick<typeof i18next, 'init' | 'use'>;
};

export type InternationalizationOptions = {
    readonly translationOverrides: Maybe<TranslationOverrides>;
};

export const useLanguage = (): {
    readonly language: LanguageConfig;
    readonly languageKey: LanguageKey;
    readonly languages: LanguageConfigMap;
} => {
    const {
        i18n: { resolvedLanguage },
    } = useTranslation();

    return isAvailableLanguage(resolvedLanguage)
        ? {
              language: availableI18nLanguages[resolvedLanguage],
              languageKey: resolvedLanguage,
              languages: availableI18nLanguages,
          }
        : {
              language: availableI18nLanguages.en,
              languageKey: 'en',
              languages: availableI18nLanguages,
          };
};

export const createInternationalization = async (
    dependencies: InternationalizationDependencies,
    options: InternationalizationOptions,
): Promise<void> => {
    const { i18nProvider } = dependencies;
    const { translationOverrides } = options;

    i18nProvider.use(LanguageDetector);
    i18nProvider.use(initReactI18next);

    await i18nProvider.init({
        ...i18nDefaultConfig,
        resources: translationOverrides.mapOr(defaultResources, ({ de = {}, en = {} }) =>
            deepmerge(defaultResources, {
                de: {
                    translation: de,
                },
                en: {
                    translation: en,
                },
            }),
        ),
    });
};
